import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../chat/ChatAvatarIcons';
import './WorkingAllianceIntroNotice.css';

const NOTICE_GAP = 12;
const NOTICE_WIDTH = 304;
const VIEWPORT_MARGIN = 12;

function getNoticePosition(anchor) {
  const rect = anchor.getBoundingClientRect();
  const fitsLeft = rect.left >= NOTICE_WIDTH + NOTICE_GAP + VIEWPORT_MARGIN;

  if (fitsLeft) {
    return {
      placement: 'left',
      top: rect.top + (rect.height / 2),
      left: rect.left - NOTICE_GAP,
    };
  }

  return {
    placement: 'below',
    top: rect.bottom + NOTICE_GAP,
    left: Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      window.innerWidth - NOTICE_WIDTH - VIEWPORT_MARGIN,
    ),
  };
}

function WorkingAllianceIntroNotice({
  anchorRef,
  theme = 'dark',
  dismissed,
  onDismissedChange,
  onClose,
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;

    const updatePosition = () => setPosition(getNoticePosition(anchor));
    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  if (!position) return null;

  return createPortal(
    <aside
      className={`wai-intro-notice wai-intro-notice--${position.placement} wai-intro-notice--${theme === 'dark' ? 'dark' : 'light'}`}
      style={{ top: position.top, left: position.left }}
      role="status"
    >
      <button
        type="button"
        className="wai-intro-notice__close"
        onClick={onClose}
        aria-label="Close working alliance introduction"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="wai-intro-notice__message">
        <span className="wai-intro-notice__avatar" aria-hidden="true">
          <AimieRobotAvatarIcon />
        </span>
        <div>
          <strong>Working Alliance</strong>
          <p>The client&apos;s working alliance can be tracked here as the session develops.</p>
        </div>
      </div>
      <label className="wai-intro-notice__dismiss">
        <input
          type="checkbox"
          checked={dismissed}
          onChange={(event) => onDismissedChange(event.target.checked)}
        />
        Click here to not show again
      </label>
    </aside>,
    document.body,
  );
}

export default WorkingAllianceIntroNotice;
